import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { 
  getMyTrainersVideos, 
  getOtherTrainersVideos, 
  checkVideoAccess 
} from '../api/videoLessonApi';
import { checkSubscriptionStatus } from '../api/subscriptionApi';
import '../styles/UserPortal.css';

const UserPortal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('my-trainers');
  const [myTrainersVideos, setMyTrainersVideos] = useState([]);
  const [otherVideos, setOtherVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessMessage, setAccessMessage] = useState('');

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    // Get token from URL or localStorage
    const params = new URLSearchParams(location.search);
    const urlToken = params.get('token');
    
    if (urlToken) {
      localStorage.setItem('userPortalAccessToken', urlToken);
      // Remove token from URL
      window.history.replaceState({}, document.title, '/user-portal');
    }

    const token = urlToken || localStorage.getItem('userPortalAccessToken');
    
    if (!token) {
      alert('No access token found. Please use the link provided from the mobile app.');
      navigate('/');
      return;
    }

    try {
      const decoded = jwtDecode(token);
      
      if (decoded.role !== 'user' || !decoded.portalAccess) {
        alert('Invalid access token. This portal is for mobile app users only.');
        navigate('/');
        return;
      }

      setUser(decoded);
      
      localStorage.setItem('userPortalAccessToken', token);
      
      fetchData();
    } catch (error) {
      console.error('Token decode error:', error);
      alert('Invalid or expired token. Please get a new link from the mobile app.');
      navigate('/');
    }
  }, [location, navigate]);

  const fetchData = async () => {
    setLoading(true);
    setSearchTerm(''); 
    try {
      const subStatus = await checkSubscriptionStatus();
      setIsSubscribed(subStatus.data?.status === 'active');

      if (activeTab === 'my-trainers') {
        const response = await getMyTrainersVideos(1, 50);
        console.log('My Trainers Response:', response);
        const videos = response.data?.videoLessons || [];
        setMyTrainersVideos(videos);
        setFilteredVideos(videos);
      } else {
        const response = await getOtherTrainersVideos(1, 50);
        console.log('Other Trainers Response:', response);
        const videos = response.data?.videoLessons || [];
        setOtherVideos(videos);
        setFilteredVideos(videos);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [activeTab, user]);

  const handleVideoClick = async (video) => {
    try {
      const accessResponse = await checkVideoAccess(video.id);
      
      if (accessResponse.data.hasAccess) {
        setSelectedVideo(video);
        setShowVideoModal(true);
        setAccessDenied(false);
      } else {
        setAccessMessage(accessResponse.data.message);
        setAccessDenied(true);
        setShowVideoModal(true);
      }
    } catch (error) {
      console.error('Error checking video access:', error);
      alert('Error checking video access. Please try again.');
    }
  };

  const handleCloseVideo = () => {
    setShowVideoModal(false);
    setSelectedVideo(null);
    setAccessDenied(false);
    setAccessMessage('');
  };

  const handleSubscribeClick = () => {
    navigate('/subscription');
  };

  const handleLogout = () => {
    localStorage.removeItem('userPortalAccessToken');
    navigate('/');
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    const currentVideos = activeTab === 'my-trainers' ? myTrainersVideos : otherVideos;

    if (!value.trim()) {
      setFilteredVideos(currentVideos);
      return;
    }

    const searchLower = value.toLowerCase();
    const filtered = currentVideos.filter((video) => {
      const titleMatch = video.title?.toLowerCase().includes(searchLower);
      const descriptionMatch = video.description?.toLowerCase().includes(searchLower);
      const trainerNameMatch = video.trainer?.name?.toLowerCase().includes(searchLower);
      const trainerEmailMatch = video.trainer?.email?.toLowerCase().includes(searchLower);
      
      return titleMatch || descriptionMatch || trainerNameMatch || trainerEmailMatch;
    });

    setFilteredVideos(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredVideos(activeTab === 'my-trainers' ? myTrainersVideos : otherVideos);
  };

  if (!user) {
    return (
      <div className="user-portal-loading">
        <div className="loading-spinner"></div>
        <p>Verifying access...</p>
      </div>
    );
  }

  return (
    <div className="user-portal-container">
      {/* Header */}
      <div className="user-portal-header">
        <div className="header-content">
          <div className="user-info">
            <h1>Video Library</h1>
            <p className="welcome-text">Welcome, {user.userName}</p>
          </div>
          <div className="header-actions">
            {!isSubscribed && (
              <button className="subscribe-button" onClick={handleSubscribeClick}>
                Subscribe Now
              </button>
            )}
            {isSubscribed && (
              <div className="premium-badge">Premium Member</div>
            )}
            <button className="subscription-button" onClick={() => navigate('/subscription')}>
              My Subscription
            </button>
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button 
          className={`tab ${activeTab === 'my-trainers' ? 'active' : ''}`}
          onClick={() => setActiveTab('my-trainers')}
        >
          My Trainer's Videos
        </button>
        <button 
          className={`tab ${activeTab === 'other-trainers' ? 'active' : ''}`}
          onClick={() => setActiveTab('other-trainers')}
        >
          Other Trainers
        </button>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by title, description, trainer name or email..."
            value={searchTerm}
            onChange={handleSearch}
          />
          {searchTerm && (
            <button className="clear-search-button" onClick={handleClearSearch}>
              ×
            </button>
          )}
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        {searchTerm && (
          <div className="search-results-info">
            {filteredVideos.length} result{filteredVideos.length !== 1 ? 's' : ''} found
          </div>
        )}
      </div>

      {/* Content */}
      <div className="portal-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading videos...</p>
          </div>
        ) : filteredVideos.length === 0 ? (
          <div className="empty-state">
            <p>{searchTerm ? 'No videos found matching your search' : 'No videos available'}</p>
            {activeTab === 'other-trainers' && !isSubscribed && !searchTerm && (
              <button className="subscribe-empty-button" onClick={handleSubscribeClick}>
                Subscribe to Access More Videos
              </button>
            )}
            {searchTerm && (
              <button className="clear-search-empty-button" onClick={handleClearSearch}>
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div className="videos-grid">
            {filteredVideos.map((video) => (
              <div 
                key={video.id} 
                className="video-card"
                onClick={() => handleVideoClick(video)}
              >
                <div className="video-thumbnail">
                  {video.thumbnailUrl ? (
                    <img 
                      src={video.thumbnailUrl} 
                      alt={video.title}
                      className="video-thumbnail-image"
                    />
                  ) : (
                    <div className="video-thumbnail-placeholder">
                      <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{width: '60px', height: '60px', opacity: 0.3}}>
                        <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                      </svg>
                    </div>
                  )}
                  <div className="play-overlay">
                    <div className="play-button">
                      <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                  {activeTab === 'other-trainers' && !isSubscribed && (
                    <div className="premium-overlay">
                      <div className="premium-text">PREMIUM</div>
                    </div>
                  )}
                </div>
                <div className="video-details">
                  <h3 className="video-title">{video.title}</h3>
                  <p className="video-description">{video.description}</p>
                  <div className="video-meta">
                    <div className="trainer-info">
                      <span className="trainer-name">{video.trainer?.name || 'Trainer'}</span>
                      <span className="trainer-email">{video.trainer?.email || ''}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <span className="video-views">{video.viewCount || 0} views</span>
                      <span>{formatDuration(video.duration)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Video Modal */}
      {showVideoModal && !accessDenied && selectedVideo && (
        <div className="video-modal" onClick={handleCloseVideo}>
          <div className="modal-content video-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={handleCloseVideo}>×</button>
            <h2>{selectedVideo.title}</h2>
            <video 
              src={selectedVideo.videoUrl} 
              controls 
              autoPlay
              className="modal-video"
            />
            <div className="video-info">
              <p className="video-description">{selectedVideo.description}</p>
              <div className="video-meta-modal">
                <div className="trainer-details">
                  <strong>Trainer:</strong>
                  <div className="trainer-contact">
                    <span className="trainer-name">{selectedVideo.trainer?.name || 'N/A'}</span>
                    <span className="trainer-email">{selectedVideo.trainer?.email || ''}</span>
                  </div>
                </div>
                <div className="video-stats">
                  <span>{selectedVideo.viewCount || 0} views</span>
                  <span>{formatDuration(selectedVideo.duration)}</span>
                  <span>{new Date(selectedVideo.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Access Denied Modal */}
      {showVideoModal && accessDenied && (
        <div className="video-modal" onClick={handleCloseVideo}>
          <div className="modal-content access-denied-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={handleCloseVideo}>×</button>
            <h2>Subscription Required</h2>
            <p className="access-message">{accessMessage}</p>
            <p className="access-info">Subscribe now to unlock all video content from all trainers</p>
            <div className="benefits-list">
              <div className="benefit-item">Unlimited access to all videos</div>
              <div className="benefit-item">HD Quality streaming</div>
              <div className="benefit-item">New content every week</div>
            </div>
            <button className="subscribe-modal-button" onClick={handleSubscribeClick}>
              Subscribe Now
            </button>
            <button className="cancel-modal-button" onClick={handleCloseVideo}>
              Maybe Later
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPortal;

